import {
  DEFAULT_EVERGREEN_DURATION_UNIT,
  DEFAULT_EVERGREEN_START_EVENT,
  DEFAULT_THRESHOLD_UNIT,
  DEFAULT_TIME_THRESHOLD_UNIT,
  DEFAULT_VALUE_UNIT,
  DEFAULT_VIP_APPLY_BY,
  DURATION_UNIT_MULTIPLIERS,
  LTO_FORM_KEYS,
  OFFER_TARGET_TYPE,
  OFFER_TYPE,
  ONE_HOUR_IN_SECONDS,
  TIMER_TYPE,
} from "../constants";
import {
  OFFER_DIRECTION,
  TRIGGER_TYPE,
  getDefaultShowAtCheckout,
} from "./unifiedOffer";
import {
  mapDirectionFromBackend,
  mapTriggerFromBackend,
  rowsFromTriggerConfig,
} from "./offerContract";

export const normalizeTimerType = (val) =>
  val === TIMER_TYPE.EVERGREEN ? TIMER_TYPE.EVERGREEN : TIMER_TYPE.FIXED;

export const normalizeOfferType = (val) =>
  val === OFFER_TYPE.PERCENTAGE ? OFFER_TYPE.PERCENTAGE : OFFER_TYPE.AMOUNT;

export const getRecordListingUids = (recordData) => {
  if (Array.isArray(recordData?.listing_uids)) {
    return recordData.listing_uids.map((l) =>
      typeof l === "string" ? l : normalizeListingRef(l).uuid
    );
  }
  return recordData?.listing_uid ? [recordData.listing_uid] : [];
};

/**
 * Canonical payment-page ref. List rows use
 * `{ dynamic_link_id, uuid, title }` but `host/offer/detail` serializes
 * Django-style relations as `{ dynamic_link__dynamic_link_id,
 * dynamic_link__title }`. Normalize once so every downstream reader
 * (initial values, title maps, option builders) sees one shape.
 */
export const normalizePaymentPageRef = (d) => {
  if (typeof d === "string" || d === null || d === undefined) {
    return { dynamic_link_id: d, uuid: d, title: d };
  }
  const id =
    d.dynamic_link_id ??
    d.dynamic_link__dynamic_link_id ??
    d.uuid ??
    d.value ??
    "";
  return {
    ...d,
    dynamic_link_id: id,
    uuid: d.uuid ?? id,
    title: d.title ?? d.dynamic_link__title ?? id,
  };
};

/** Same idea for offering refs (`listing__uuid` / `listing__title`). */
export const normalizeListingRef = (l) => {
  if (typeof l === "string" || l === null || l === undefined) return l;
  const uuid = l.uuid ?? l.listing__uuid ?? l.value ?? "";
  const title = l.title ?? l.listing__title ?? uuid;
  return { ...l, uuid, title };
};

export const normalizeComboPages = (pages) => {
  if (!pages) return [];
  return pages.map((cp) => normalizePaymentPageRef(cp));
};

/**
 * Merge a `host/offer/detail` response over the list/session row before
 * prefilling. Detail top-level keys (direction, trigger_type,
 * show_at_checkout, sync flag, price, trigger_config, locks) win; the
 * session metadata snapshot (exact UI strings/rows) is preserved since
 * detail carries no `metadata` key. Relational arrays are normalized to
 * the canonical list-row shape (see normalizePaymentPageRef).
 */
export const mergeDetailIntoRecord = (record = {}, detail = {}) => {
  if (!detail || typeof detail !== "object" || !detail.uuid) return record;
  const merged = { ...record, ...detail };
  if (Array.isArray(detail.dynamic_link_uuids)) {
    merged.dynamic_link_uuids = detail.dynamic_link_uuids.map(
      normalizePaymentPageRef
    );
  }
  if (Array.isArray(detail.listing_uids)) {
    merged.listing_uids = detail.listing_uids.map(normalizeListingRef);
  }
  if (Array.isArray(detail.combo_pages)) {
    merged.combo_pages = normalizeComboPages(detail.combo_pages);
  }
  return merged;
};

/**
 * Calendar-day equality for the edit submit path. Never throws: non-Date
 * inputs (null datetimes, blank fallbacks, garbage strings) compare as
 * NOT equal so the caller sends explicit form dates — the safe direction.
 * (Regression: "".getDate() threw "date.getDate is not a function".)
 */
export const compareDates = (date, todaysDate) => {
  if (typeof todaysDate === "string" && todaysDate?.includes("-"))
    todaysDate = new Date(todaysDate);
  if (
    date instanceof Date &&
    todaysDate instanceof Date &&
    !Number.isNaN(date.getTime()) &&
    !Number.isNaN(todaysDate.getTime())
  ) {
    return (
      date.getDate() === todaysDate.getDate() &&
      date.getFullYear() === todaysDate.getFullYear() &&
      date.getMonth() === todaysDate.getMonth()
    );
  }
  return false;
};

/**
 * Build the form's initial values when EDITING an existing offer.
 * Prefills every unified field from the saved record so the edit modal opens
 * with the exact previously-saved selection. Pure (date parsing is injected).
 */
export const buildEditInitialValues = ({
  recordData = {},
  baseInitialValues = {},
  dateConversion,
}) => {
  // Datetimes can be null on list rows (open-ended / evergreen offers) —
  // fall back to the form defaults instead of throwing inside the parser
  // (prod crash: Cannot read properties of undefined reading '0').
  const start = recordData.start_datetime
    ? dateConversion(recordData.start_datetime)
    : {
        date: baseInitialValues?.start_date,
        time: baseInitialValues?.start_time,
      };
  const end = recordData.end_datetime
    ? dateConversion(recordData.end_datetime)
    : {
        date: baseInitialValues?.end_date,
        time: baseInitialValues?.end_time,
      };
  const listingUids = getRecordListingUids(recordData);
  const normalizedComboPages = normalizeComboPages(recordData.combo_pages);

  const metadataDurationSeconds =
    recordData?.metadata?.evergreen_duration_seconds;
  const metadataDurationUnit =
    recordData?.metadata?.evergreen_duration_unit ||
    DEFAULT_EVERGREEN_DURATION_UNIT;
  const displayValue = metadataDurationSeconds
    ? metadataDurationSeconds /
      (ONE_HOUR_IN_SECONDS *
        (DURATION_UNIT_MULTIPLIERS[metadataDurationUnit] || 1))
    : baseInitialValues?.[LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE];

  // Backend-acted top-level values win over the metadata snapshot; the
  // metadata string is the fallback (exact family label for our own creates).
  const metadataOfferType = normalizeOfferType(
    recordData?.metadata?.offer_type
  );
  const isPercentageOffer = metadataOfferType === OFFER_TYPE.PERCENTAGE;
  const direction =
    mapDirectionFromBackend(recordData?.direction) ||
    recordData?.metadata?.direction ||
    OFFER_DIRECTION.DISCOUNT;
  const triggerType =
    recordData?.metadata?.trigger_type ||
    mapTriggerFromBackend(recordData?.trigger_type) ||
    TRIGGER_TYPE.SCHEDULE;
  // trigger_config (the stored column) backs every builder fallback: backend
  // drops client metadata on create, so metadata rows may be absent while the
  // validated config is always persisted.
  const storedConfig =
    recordData?.trigger_config && typeof recordData.trigger_config === "object"
      ? recordData.trigger_config
      : {};
  const vipApplyBy =
    recordData?.metadata?.vip_apply_by ||
    (storedConfig.interval_type === 1
      ? "date"
      : storedConfig.interval_type === 2
      ? "weekday"
      : null) ||
    DEFAULT_VIP_APPLY_BY;
  // Builders refill from the stored trigger_config when metadata rows are
  // absent (backend drops client metadata on create).
  const savedRows = rowsFromTriggerConfig(recordData?.trigger_config);
  const savedTiers =
    recordData?.metadata?.tiers?.length > 0
      ? recordData.metadata.tiers
      : savedRows.tiers;
  const savedVipWindows =
    recordData?.metadata?.vip_windows?.length > 0
      ? recordData.metadata.vip_windows
      : savedRows.vip_windows;
  const savedBatchValues =
    recordData?.metadata?.batch_values?.length > 0
      ? recordData.metadata.batch_values
      : savedRows.batch_values;
  // Any stored per-row international value means the offer has a separate
  // international ladder — auto-check the toggle so the column shows on edit.
  const hasRowInternational = [
    ...savedTiers,
    ...savedVipWindows,
    ...savedBatchValues,
    ...(storedConfig?.tiers || []),
    ...(storedConfig?.rows || []),
    ...(storedConfig?.schedules || []),
    ...(storedConfig?.default ? [storedConfig.default] : []),
  ].some(
    (row) =>
      row?.value_international !== undefined ||
      row?.markup_international !== undefined
  );

  return {
    ...baseInitialValues,
    target_type: recordData?.target_type || baseInitialValues?.target_type,
    [LTO_FORM_KEYS.DIRECTION]: direction,
    [LTO_FORM_KEYS.TRIGGER_TYPE]: triggerType,
    [LTO_FORM_KEYS.TIERS]: savedTiers.length
      ? savedTiers
      : [
          {
            threshold: "",
            threshold_unit: DEFAULT_TIME_THRESHOLD_UNIT,
            value: "",
          },
        ],
    [LTO_FORM_KEYS.THRESHOLD_UNIT]:
      recordData?.metadata?.threshold_unit ||
      storedConfig.threshold_unit ||
      DEFAULT_THRESHOLD_UNIT,
    [LTO_FORM_KEYS.VALUE_UNIT]:
      recordData?.metadata?.value_unit ||
      storedConfig.value_unit ||
      DEFAULT_VALUE_UNIT,
    [LTO_FORM_KEYS.SHOW_AT_CHECKOUT]:
      recordData?.show_at_checkout ??
      recordData?.metadata?.show_at_checkout ??
      getDefaultShowAtCheckout(direction),
    [LTO_FORM_KEYS.SYNC_END_TO_START]:
      recordData?.sync_end_to_offering_start ??
      recordData?.metadata?.sync_end_to_offering_start ??
      recordData?.metadata?.sync_end_to_start ??
      false,
    [LTO_FORM_KEYS.EVERGREEN_START_EVENT]:
      recordData?.metadata?.evergreen_start_event ||
      DEFAULT_EVERGREEN_START_EVENT,
    [LTO_FORM_KEYS.VIP_APPLY_BY]: vipApplyBy,
    [LTO_FORM_KEYS.VIP_WINDOWS]: savedVipWindows,
    [LTO_FORM_KEYS.VIP_START_DATE]:
      recordData?.metadata?.vip_start_date || storedConfig.start_date || "",
    [LTO_FORM_KEYS.VIP_END_DATE]:
      recordData?.metadata?.vip_end_date || storedConfig.end_date || "",
    [LTO_FORM_KEYS.BATCH_VALUES]: savedBatchValues,
    bookingCount: recordData?.bookingCount ?? recordData?.total_bookings ?? 0,
    [LTO_FORM_KEYS.OFFER_TYPE]: metadataOfferType,
    [LTO_FORM_KEYS.TIMER_TYPE]: (() => {
      // Evergreen is a trigger. Legacy records may only carry
      // `metadata.timer_type`, so honour it for the base schedule trigger —
      // but never for threshold triggers (stale value would leak evergreen
      // fields into the payload).
      const legacyEvergreen =
        normalizeTimerType(recordData?.metadata?.timer_type) ===
        TIMER_TYPE.EVERGREEN;
      const evergreen =
        triggerType === TRIGGER_TYPE.EVERGREEN ||
        (triggerType === TRIGGER_TYPE.SCHEDULE && legacyEvergreen);
      return evergreen ? TIMER_TYPE.EVERGREEN : TIMER_TYPE.FIXED;
    })(),
    [LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE]: displayValue,
    [LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT]: metadataDurationUnit,
    [LTO_FORM_KEYS.INR_PRICE]: isPercentageOffer ? null : recordData.price,
    [LTO_FORM_KEYS.USD_CHECKBOX]:
      (!isPercentageOffer && !!recordData.price_international) ||
      hasRowInternational,
    [LTO_FORM_KEYS.USD_PRICE]:
      !isPercentageOffer && recordData.price_international
        ? parseFloat(recordData.price_international).toFixed(2)
        : null,
    [LTO_FORM_KEYS.PERCENTAGE_VALUE]: isPercentageOffer
      ? recordData?.metadata?.discount_percentage
      : null,
    [LTO_FORM_KEYS.LISTINGS]: listingUids,
    offerName: recordData.title,
    start_date: start.date,
    start_time: start.time,
    end_date: end.date,
    end_time: end.time,
    region: recordData.region,
    ...(recordData.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE && {
      combo_pages: normalizedComboPages,
      [LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]: Array.isArray(
        recordData.dynamic_link_uuids
      )
        ? recordData.dynamic_link_uuids.map(
            (d) => normalizePaymentPageRef(d).dynamic_link_id
          )
        : normalizedComboPages.map((cp) => cp.dynamic_link_id).filter(Boolean),
    }),
  };
};
